import { Injectable, inject } from '@angular/core';
import { assert } from '../../../../core/assert/assert';
import { TimeBlockDayOrWeekFixIntervalType } from '../../../../core/models/timeblock/time-block-view-type.model';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { insertByOrder } from '../../../../shared/functions/array-functions';
import { mapToArray } from '../../../../shared/functions/map-functions';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { TodoPlannerService } from '../../services/todo-planner.service';
import { TodoEntryComponentHandlerService } from './todo-entry-component-handler.service';
import { TodoEntryService } from './todo-entry.service';

@Injectable({
  providedIn: 'root',
})
export class TodoEntryCrudService {
  // DI
  private readonly todoEntryComponentHandlerService = inject(TodoEntryComponentHandlerService);
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoEntryService = inject(TodoEntryService);

  /**
   * Returns a writable shallow copy of the planner event model.
   */
  public selectTodoEntry(todoEntry: TodoEntryModel | number): TodoEntryModel {
    const id = typeof todoEntry === 'number' ? todoEntry : todoEntry.id;

    const { fixIntervalTodoEntryModels, variableIntervalTodoEntryModels } =
      this.todoPlannerService.todoPlannerModel;

    const allEntries = [
      ...mapToArray(fixIntervalTodoEntryModels, true),
      ...mapToArray(variableIntervalTodoEntryModels, true),
    ] as TodoEntryModel[];

    const found = allEntries.find((p) => p.id === id);

    assert(!!found, 'Todo entry was not found.');

    // preserve your previous behavior: only clone when input was a model
    return TodoEntryService.cloneTodoModel(found, false);
  }

  /**
   * Add planner event model to the collection and build component
   */
  public addTodoEntry(newTodoEntryModel: TodoEntryModel, detectChanges = false): TodoEntryModel {
    // If to-do entry is completely outside the view, don't insert it into the model and don't render it.
    if (this.todoEntryService.isOutsideOfView(newTodoEntryModel)) {
      return null;
    }

    const clonedModel = TodoEntryService.cloneTodoModel(newTodoEntryModel);
    if (clonedModel.fixInterval) {
      clonedModel.timeBlockViewType = new TimeBlockDayOrWeekFixIntervalType();
    }

    const dateKey = DateTimeHelper.format(
      this.todoEntryService.getCorrectContainerDate(clonedModel.start),
    );
    const container = this.todoPlannerService.getTodoEntryModels(clonedModel);
    const todoEntriesInContainer = container.get(dateKey);

    const targetIndex = insertByOrder(todoEntriesInContainer, clonedModel);

    clonedModel.componentRef = this.todoEntryComponentHandlerService.buildComponent(
      clonedModel,
      targetIndex,
      detectChanges,
    );

    return clonedModel;
  }

  /**
   * Update to-do entry model in the collection
   */
  public updateTodoEntry(updatedTodoEntry: TodoEntryModel): void {
    const todoEntries = this.todoPlannerService.getTodoEntryModels(updatedTodoEntry);
    const todoEntriesInLane = todoEntries.get(DateTimeHelper.format(updatedTodoEntry.start));
    const index = todoEntriesInLane.findIndex((entry) => entry.id === updatedTodoEntry.id);
    assert(index >= 0, 'To-do entry was not found.');

    todoEntriesInLane.splice(index, 1, updatedTodoEntry);
  }

  /**
   * Remove to-do entry model from the collection, update sorted indices and destroy component.
   */
  public removeTodoEntry(todoEntryModel: TodoEntryModel): void {
    this.todoEntryComponentHandlerService.removeComponent(todoEntryModel.componentRef);
    const todoEntryPosition = this.todoEntryService.getTodoEntryPositionInLane(todoEntryModel);

    const todoEntryModels = this.todoPlannerService.getTodoEntryModels(todoEntryModel);
    const laneStart = DateTimeHelper.format(
      this.todoEntryService.getCorrectContainerDate(todoEntryModel.start),
    );
    const laneTodoEntries = todoEntryModels.get(laneStart);
    laneTodoEntries.splice(todoEntryPosition, 1);
  }
}
